import axios from 'axios';
// import * as Sentry from '@sentry/vue';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import proxy from '@/config/proxy';
import type { ParamsType } from '@/config/proxy';
import { getCookie } from '@/utils/cookie';
import { isApp, isPC } from '@/utils/tools';
// import { getTimeZone } from '@/utils/time';
import { pinia } from '@/store/index';
import { useUserStore } from '@/store/User';
import { getLanguage } from '@/lang/i18n';
import { useCrypto } from '@/utils/crypto';
import { checkUserExp } from '@/utils/user-exp';
import { slowRequestManager } from '@/utils/slowRequestManager';

const userStore = useUserStore(pinia);

const Axios = axios.create({
  timeout: 30000, // 30秒
  // headers: {
  //   'Accept-Language': getLanguage().toLowerCase()
  // },
  // 跨越请求凭证
  withCredentials: true
});

const matchConfig = (config: AxiosRequestConfig): ParamsType => {
  const cache: ParamsType = {
    url: config.url || ''
  };

  Object.keys(proxy).some(key => {
    if (cache.url.indexOf(key) === 0) {
      const { url, headers = {}, params, responseHandle } = proxy[key];

      cache.url = cache.url.replace(key, url);
      cache.headers =
        typeof headers === 'function' ? headers(config.headers) : headers;
      cache.params =
        typeof params === 'function' ? params(config.params) : params;
      cache.responseHandle =
        typeof responseHandle === 'function' ? responseHandle : undefined;

      return true;
    }

    return false;
  });
  return cache;
};

// type TypeRequestHandle = {
//   responseHandle?: ParamsType['responseHandle'];
// } & AxiosRequestConfig;

/**
 * 请求参数处理
 *
 * @param {object} config 请求参数
 * @returns {object} 处理后的参数
 */
// 添加请求开始时间
const requestHandle = (config: any) => {
  // 记录请求开始时间
  config.requestStartTime = Date.now();

  const { url, headers, params, responseHandle } = matchConfig(config);
  config.url = url;
  config.responseHandle = responseHandle;

  const headersObj: any = {
    ...config.headers,
    'Accept-Language': getLanguage().toLowerCase()
  };
  config.headers = headersObj;

  const { isTg } = usePlat();
  // config.headers.Platform = 'web';
  // 如果是H5设置headers
  if (!isPC()) {
    config.headers.Platform = 'h5';
    config.headers.osType = 'h5';
  }
  if (isTg) {
    config.headers.Platform = 'tg';
  }

  // 设置post请求时默认Content-Type
  if (config.method === 'post' && !config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'multipart/form-data';
  }

  // 处理 headers
  if (headers) {
    config.headers = { ...headers, ...config.headers };
  }

  // 处理 params
  if (params) {
    config.params = { ...params, ...config.params };
  }

  config.params = {
    ...config.params,
    r: Math.random().toString(32).substring(2),
    __test__: window.location.href,
    c_token: getCookie('c_token'),
    Platform: isApp() ? 'h5' : ''
  };
  if (config.crypto && import.meta.env.VITE_CRYPTO_SWITCH !== 'close') {
    const { addCryptoHeaders } = useCrypto();
    config.headers = addCryptoHeaders(config);
  }
  return config;
};

/**
 * 请求结果处理
 *
 * @param {object} response axios返回的结果
 * @returns {object} 处理之后的结果
 */
const responseHandle = (response: AxiosResponse | any) => {
  try {
    // 计算接口耗时并追踪
    const requestTime = Date.now() - (response.config.requestStartTime || 0);
    slowRequestManager.trackRequest(requestTime, response.config);
  } catch (error) {
    console.log('Error tracking request:', error);
  }

  const data = { ...response.data };
  type IPlanTagProps = {
    success: boolean;
    code?: number;
    message?: string;
    data: object;
    msgArgs: object;
  };

  const newData: IPlanTagProps = {
    success: response.data && response.data.code ? response.data.code === 200 : response.status === 200,
    code: response.data && response.data.code ? response.data.code : response.status,
    message: response.data && response.data.msg ? response.data.msg : (response.message || response.msg || response.error || 'success'),
    data: response.data && response.data.data ? response.data.data : response.data, // 处理返回成功时带code情况
    msgArgs: response.data?.msgArgs || [] // 返回错误时需要的数据，message有时不能满足
  };

  // const newData: IPlanTagProps = {
  //     success:  data.success || (data.code >= 200 && data.code <= 300) || data.status === 'ok' || (data.code ? false : true),
  //     code: data.code || 200,
  //     message: data.message || data.error || 'success',
  //     data: response.data,
  // };
  // 如果代理有处理相应函数优先使用
  // eslint-disable-next-line
  // @ts-ignore

  checkUserExp(response);

  if (response.config.responseHandle) {
    // eslint-disable-next-line
    // @ts-ignore
    return response.config.responseHandle(data, response);
  }

  return newData;
};

/**
 * 错误处理
 *
 * @returns {object} 处理之后的结果
 */
const errorHandle = (error: any) => {
  if (error?.request?.responseURL?.indexOf('/user/get_base_info') <= 0
    && error?.response?.data?.code === 30000
  ) {
    if (!isApp()) {
      userStore.logOut();
    }
  }

  // Sentry.captureException(new Error('API Error'), {
  //   level: 'error',
  //   tags: {
  //     timeZone: getTimeZone()
  //   },
  //   extra: {
  //     userUid: sessionStorage.getItem('UID'),
  //     timestamp: new Date().toISOString(),
  //     timeZone: getTimeZone(),
  //     errorInfo: error
  //   }
  // });

  return {
    success: false,
    code: (error?.response?.status && String(error.response.status).startsWith('5')) ? error.response.status : (error?.response?.data?.code || 500),
    data: null,
    message: error?.response?.data?.msg || 'Access error, please try again later',
    error,
    msgArgs: error.response?.data?.msgArgs || []
  };
};

// 请求参数处理
Axios.interceptors.request.use(
  config => requestHandle(config),
  error => errorHandle(error)
);

// 响应数据处理
Axios.interceptors.response.use(
  response => responseHandle(response),
  error => errorHandle(error)
);

export default Axios;

